import { useNotification } from 'admin';
import { TrackedEvents, trackEvent } from 'analytics';
import {
  type Incoterms,
  type MatchedSiteMaterialV2,
  useApiAccountsV1AccountManagerNotifyCreate,
  useApiAccountsV1PortsRetrieve,
  useApiTradeV1TradeCompaniesRetrieve,
  useCurrentUser,
} from 'api';
import { getFirstName } from 'features';
import { getCountryNameByCountryAbbr } from 'form';
import { logger } from 'monitor';
import { Button } from 'ui';

export const ShareMatch = ({
  siteMaterial,
  matchedTradeCompanyId,
  buyerName,
  buyerCountry,
  demandSignalUrl,
  supplySignalUrl,
  offerPrice,
  incoterm,
  port_id,
}: {
  siteMaterial: MatchedSiteMaterialV2;
  matchedTradeCompanyId: number;
  buyerName: string;
  buyerCountry?: string;
  offerPrice?: string;
  demandSignalUrl?: string;
  supplySignalUrl?: string;
  incoterm: Incoterms;
  port_id?: number;
}) => {
  const { open } = useNotification();
  const { data: user } = useCurrentUser();
  const { data: tradeCompany, isLoading } = useApiTradeV1TradeCompaniesRetrieve(matchedTradeCompanyId);
  const { data: port } = useApiAccountsV1PortsRetrieve(port_id || 0, {
    query: {
      enabled: Boolean(port_id),
    },
  });
  const { mutate: notifyAccountManager, isPending } = useApiAccountsV1AccountManagerNotifyCreate({
    mutation: {
      onError: () => {
        open?.({ message: 'Failed to notify account manager', type: 'error' });
      },
      onSuccess: () => {
        open?.({ message: 'Successfully notified account manager', type: 'success' });
      },
    },
  });
  const qualityReportUrl = new URL(`/report/${siteMaterial.public_id}/`, window.location.origin).href;

  return (
    <Button
      loading={isPending || isLoading}
      variant="secondary"
      onClick={() => {
        if (!tradeCompany?.account_manager) {
          logger.error('No account manager found for trade company');
          return;
        }

        trackEvent(TrackedEvents.TRADING_DASHBOARD_SHARE_WITH_AM, {
          accountManager: tradeCompany?.account_manager,
          materialName: siteMaterial.material_spec.title,
          tradeCompany: tradeCompany,
        });

        notifyAccountManager({
          accountManagerId: tradeCompany?.account_manager?.id,
          data: {
            message: `Hey ${getFirstName(tradeCompany.account_manager.short_name)},

Potential match found for ${siteMaterial.site.trade_company.name} for ${siteMaterial.grade.name} ${siteMaterial.material_spec.title} with ${buyerName} ${buyerCountry ? `(${getCountryNameByCountryAbbr(buyerCountry)})` : ''}

<${qualityReportUrl}|Quality Report>
${supplySignalUrl ? `<${supplySignalUrl}|Supply Signal>` : ''}
${demandSignalUrl ? `<${demandSignalUrl}|Demand Signal>` : ''}

Please review if potential deal possible at ${offerPrice} ${incoterm}${port?.name ? ` ${port.name}` : ''}. Can you get an updated signal?

Thanks,
${user?.full_name ? getFirstName(user.full_name) : ''}
			`,
          },
        });
      }}
    >
      Share match with{' '}
      {tradeCompany?.account_manager?.short_name ? getFirstName(tradeCompany.account_manager.short_name) : 'AM'}
    </Button>
  );
};
